import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
  Provider
} from '@angular/core';
import {provideRouter, Router} from '@angular/router';
import {provideTanStackQuery, QueryClient} from '@tanstack/angular-query-experimental';
import {provideAnimations} from '@angular/platform-browser/animations';
import {HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi, withJsonpSupport} from '@angular/common/http';
import {DATE_PIPE_DEFAULT_OPTIONS} from '@angular/common';
import {OVERLAY_DEFAULT_CONFIG} from '@angular/cdk/overlay';

import {routes} from './app-routing.module';
import {ConfigDgTable, ConfigDynamicTable} from '@datagrupo2/dg-ng-util';
import {configLocalDynamicTable} from './core/config/dg-ng-util/config-local-dynamic-table';
import {configLocalDgTable} from './core/config/dg-ng-util/config-local-dg-table';
import {NgxMaskConfig as IConfig, provideEnvironmentNgxMask, provideNgxMask} from 'ngx-mask';
import {SweetAlert2Module} from '@sweetalert2/ngx-sweetalert2';
import {PixelModule} from './lib/pixel/pixel.module';

import {SessionService} from './services/session-service/session.service';
import {CarrinhoIngressoService} from './services/carrinho-ingresso/carrinho-ingresso.service';
import {AddTokenInterceptor} from './core/interceptors/add-token/add-token.interceptor';
import {AddHeaderVersionInterceptor} from './core/interceptors/add-header-version/add-header-version.interceptor';
import {ApiV3BaseUrlInterceptor} from './core/interceptors/api-v3-base-url/api-v3-base-url.interceptor';
import {TranslatePipe} from './shared/custom-pipes/translate.pipe';
import {environment} from 'src/environments/environment';
import * as Sentry from '@sentry/angular';
import {createEventoAbility, EventoAbility} from "./pages/private/eventos/eventos.ability";
import {isSentryEnabled} from './core/config/sentry-enabled';
import {AnalyticsService} from './lib/analytics/analytics.service';


const sentryProviders: Provider[] = isSentryEnabled() ? [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler()
  },
  {provide: Sentry.TraceService, deps: [Router]},
] : [];

ConfigDynamicTable.set(configLocalDynamicTable);
ConfigDgTable.set(configLocalDgTable);

const maskConfig: Partial<IConfig> = {validation: false};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Exigido pelos hooks gerados pelo orval (client `angular-query`): sem um
    // QueryClient provido, todo injectQuery/injectMutation estoura em runtime.
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            // O cliente imperativo que estas telas substituem fazia UMA tentativa e não
            // refazia nada ao voltar para a aba. Os defaults do TanStack (3 retentativas,
            // refetch no foco) mudariam isso em silêncio a cada tela migrada — e a maioria
            // das operações v3 tipa 401/403, que retentar só multiplica por 4.
            retry: false,
            refetchOnWindowFocus: false,
          },
        },
      }),
    ),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
    provideNgxMask(maskConfig),
    // Garante ngx-mask disponível app-wide (usado por telas do Portal Admin).
    provideEnvironmentNgxMask(maskConfig),
    importProvidersFrom(
      PixelModule.forRoot({enabled: true, pixelId: [environment.guedderPixelId]}),
      SweetAlert2Module.forRoot(),
    ),
    SessionService,
    CarrinhoIngressoService,
    {provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: {timezone: '-0300'}},
    // Overlays do CDK (sheet, dialog, menu do spartan) voltam a empilhar por
    // z-index em vez da Popover API. Com `usePopover` ligado — o padrão do
    // Angular 21 — o painel vai para o *top layer* do navegador, que fica acima
    // de QUALQUER z-index: medido em dev, o loader do SweetAlert2 continuava
    // atrás do sheet da impressora mesmo com `z-index: 99999`. O app inteiro
    // (Bootstrap legado, wizard em 1155, `.swal2-container` em 2000) empilha por
    // z-index, então é o top layer que está fora do padrão daqui, não o resto.
    {provide: OVERLAY_DEFAULT_CONFIG, useValue: {usePopover: false}},
    {provide: HTTP_INTERCEPTORS, useClass: ApiV3BaseUrlInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AddTokenInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AddHeaderVersionInterceptor, multi: true},
    ...sentryProviders,
    {provide: EventoAbility, useValue: createEventoAbility()},
    // Antes do AppComponent (que chama session.setUser()); inerte sem posthogKey.
    provideAppInitializer(() => inject(AnalyticsService).init()),
    provideAppInitializer(() => {
      const httpBackend = inject(HttpBackend);
      return TranslatePipe.init(httpBackend)();
    }),

  ],
};
